import clsx from 'clsx';
import type * as React from 'react';

import { IS_MAC } from '@gitbook/slate-dev-environment';

export type KeyboardShortcutProps = {
    /**
     * The keyboard shortcut string.
     */
    shortcut: string;

    /**
     * The size of the keyboard shortcut representation.
     * @default 'medium'
     */
    size?: 'xsmall' | 'small' | 'medium';

    /**
     * Inverts style of the keyboard shortcut, used when the parent is dark e.g. with tooltips.
     */
    inverted?: boolean;

    /**
     * Default uses solid colors for the background, while 'nested' uses a semi-transparent background and border to blend into the parent (e.g. for shortcuts within buttons).
     * @default 'default'
     */
    kind?: 'default' | 'nested';

    /**
     * Optional styling for the keyboard shortcut.
     */
    className?: string;

    /**
     * Optional inline styling for the keyboard shortcut.
     * We discussed with design and it was decided to allow overriding to avoid many variants for single usecases.
     *
     * Overriding style should always be done after discussing with design.
     */
    style?: React.CSSProperties;
};

/**
 * Get variant-specific styles based on kind and inverted props.
 * Handles all combinations: default, nested, inverted, and nested+inverted.
 */
function getVariantStyles(kind: 'default' | 'nested', inverted?: boolean): string {
    if (kind === 'nested') {
        // Nested styles (used inside buttons) - semi-transparent to blend with parent
        return inverted
            ? 'border border-neutral-subtle/10 bg-neutral-surface/10 text-neutral-inverted-high'
            : 'border border-neutral-subtle/10 bg-neutral-surface/10 text-neutral-inverted-high';
    }

    // Default styles (standalone keyboard shortcuts)
    return inverted
        ? 'border border-[var(--neutral-11)] border-b-2 bg-[var(--neutral-12)] text-neutral-inverted-high'
        : 'border border-[var(--neutral-8)] border-b-2 bg-[var(--neutral-1)] text-neutral-high';
}

/**
 * Get size-specific styles.
 */
function getSizeStyles(size: Size): string {
    switch (size) {
        case 'xsmall':
            return 'h-4 min-w-4 text-[10px]';
        case 'small':
            return 'h-4.5 min-w-4.5 text-ui-small';
        case 'medium':
            return 'h-5 min-w-5 text-ui-small';
    }
}

/**
 * Render a keyboard shortcut.
 */
export function KeyboardShortcut(props: KeyboardShortcutProps) {
    const {
        shortcut,
        size = 'medium',
        inverted,
        kind = 'default',
        className,
        style,
        ...rest
    } = props;

    const chars = keyboardShortcutToTextArray(shortcut);

    return (
        <kbd
            // Suppress hydration issues due to OS specific keyboard modifiers
            suppressHydrationWarning
            aria-label={getAccessibleLabel(shortcut)}
            {...rest}
            className={clsx(
                // Base styles
                'flex w-fit items-center justify-center gap-1 rounded-[5px] px-1 font-normal shadow-subtle dark:[box-shadow:none]',
                // Variant styles (kind + inverted)
                getVariantStyles(kind, inverted),
                // Size styles
                getSizeStyles(size),
                className
            )}
            style={style}
        >
            {chars.map((char, index) => (
                <span
                    key={index}
                    className={charClassOverrides[char]}
                    style={getCharStyle(char, size)}
                >
                    {char}
                </span>
            ))}
        </kbd>
    );
}

/**
 * Check if a character is alphanumeric (letter or number).
 */
function isAlphanumeric(char: string): boolean {
    return /^[A-Z0-9]$/i.test(char);
}

type Size = NonNullable<KeyboardShortcutProps['size']>;

/**
 * Get style adjustments for a character at a specific size.
 * Handles both specific character overrides and general alphanumeric scaling.
 */
function getCharStyle(char: string, size: Size): React.CSSProperties | undefined {
    // Specific character overrides take precedence
    const adjustment = charStyleAdjustments[char];
    if (adjustment) {
        return adjustment[size];
    }
    // Scale down alphanumeric characters to match unicode glyph sizes
    if (isAlphanumeric(char)) {
        return { fontSize: '0.85em' };
    }
    return undefined;
}

/**
 * Per-character style adjustments to fix visual misalignment of unicode glyphs.
 * Some keyboard shortcut symbols have inconsistent vertical positioning in fonts.
 * Adjustments can be specified per-size since glyphs render differently at different sizes.
 */
const charStyleAdjustments: Record<string, Partial<Record<Size, React.CSSProperties>>> = {
    '⌘': {
        xsmall: { transform: 'scale(1.15)' },
        small: { transform: 'translateY(1px) scale(1.15)' },
        medium: { transform: 'translateY(1px) scale(1.15)' },
    },
};

/**
 * Per-character class name overrides.
 */
const charClassOverrides: Record<string, string> = {
    '↩': 'font-mono', // Enter - use monospace to avoid emoji rendering
    '⇧': 'font-system', // Shift - use font-system to avoid the otherwise bad-looking glyph
    '⌫': 'font-system', // Backspace - use font-system to avoid the otherwise bad-looking glyph
};

/**
 * Maps key names to their visual symbol and accessible label, used for both:
 * 1. visual mapping
 * 2. a11y: retrieving the accessible labels
 */
const keyMappings: Record<string, { symbol: string; label: string }> = {
    // Modifier keys
    command: { symbol: '⌘', label: 'Command' },
    cmd: { symbol: '⌘', label: 'Command' },
    control: { symbol: '⌃', label: 'Control' },
    ctrl: { symbol: '⌃', label: 'Control' },
    option: { symbol: '⌥', label: 'Option' },
    alt: { symbol: '⌥', label: 'Alt' },
    shift: { symbol: '⇧', label: 'Shift' },
    meta: { symbol: IS_MAC ? '⌘' : '^', label: IS_MAC ? 'Command' : 'Control' },

    // Action keys
    enter: { symbol: '↩', label: 'Enter' },
    return: { symbol: '↩', label: 'Return' },
    backspace: { symbol: '⌫', label: 'Backspace' },
    delete: { symbol: '⌦', label: 'Delete' },
    tab: { symbol: '⇥', label: 'Tab' },
    space: { symbol: 'Space', label: 'Space' },
    escape: { symbol: 'Esc', label: 'Escape' },
    esc: { symbol: 'Esc', label: 'Escape' },
};

/**
 * A util available for components to turn keyboard key names into their associated icon.
 */
export function prettifyShortcut(shortcut: string) {
    const lower = shortcut.toLowerCase();
    return keyMappings[lower]?.symbol ?? shortcut;
}

/**
 * Map a keyboard shortcut as pretty chars with each key in its own box
 */
export function keyboardShortcutToTextArray(shortcut: string) {
    return shortcut
        .split(/[+ ]/) // Split on a + or space character
        .filter((char) => char !== '')
        .map((part) => {
            const mapping = keyMappings[part.toLowerCase()];
            // Return symbol if mapped, otherwise uppercase the key (e.g., "k" → "K")
            return mapping?.symbol ?? part.toUpperCase();
        });
}

/**
 * Generate a human-readable label for screen readers.
 * E.g., "Cmd+Enter" → "Command plus Enter"
 */
function getAccessibleLabel(shortcut: string): string {
    return shortcut
        .toLowerCase()
        .split(/[+ ]/) // Split on a + or space character
        .filter((key) => key !== '')
        .map((key) => keyMappings[key]?.label ?? key.toUpperCase())
        .join(' plus ');
}
